export function isTableRow(text: string): boolean {
  if (/\|.*\|/.test(text)) return true;
  if (/<td[\s>]/.test(text)) return true;
  return false;
}

/**
 * Strips markdown formatting from text, removing:
 * - Table pipes (|) and HTML <td> cells - extracts only first column/cell for table rows
 * - Bold/italic markers (**, *, __, _, ***, ___)
 * - Headers (#)
 * - Links ([text](url))
 * - Code blocks and inline code (``)
 * - Strikethrough (~~)
 * - Blockquotes (>)
 * - Horizontal rules (---, ***, ___)
 * - List markers (-, *, +, 1.)
 * - Orphaned markdown characters
 * - Extra whitespace
 */
function extractFirstHtmlTableCell(text: string): string | null {
  const match = text.match(/<td[^>]*>([\s\S]*?)<\/td>/);
  if (!match) return null;
  return match[1].replace(/<[^>]+>/g, "").trim();
}

export function stripMarkdown(text: string): string {
  const firstHtmlCell = extractFirstHtmlTableCell(text);
  if (firstHtmlCell !== null) {
    text = firstHtmlCell;
  }

  return (
    text
      .replace(/<[^>]+>/g, "")
      .replace(/\[DOC:[^\]]*\]/g, "")

      .replace(/\|([^|]*)\|.*$/gm, (_, firstColumn) => firstColumn.trim())

      // Remove any remaining table pipes
      .replace(/\|/g, " ")

      // Remove bold/italic markers - more comprehensive patterns
      // Handle nested and edge cases
      .replace(/\*\*\*([^*]+)\*\*\*/g, "$1") // bold+italic
      .replace(/\*\*([^*]*)\*\*/g, "$1") // bold (allow empty content)
      .replace(/\*([^*]*)\*/g, "$1") // italic (allow empty content)
      .replace(/___([^_]+)___/g, "$1") // bold+italic with underscores
      .replace(/__([^_]*)__/g, "$1") // bold with underscores (allow empty)
      .replace(/_([^_]*)_/g, "$1") // italic with underscores (allow empty)

      // Remove any remaining orphaned asterisks and underscores
      .replace(/\*+/g, "")
      .replace(/_+/g, "")

      // Remove headers
      .replace(/^#{1,6}\s+/gm, "")

      // Remove links - keep only the text part
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")

      // Remove code blocks and inline code
      .replace(/```[\s\S]*?```/g, "")
      .replace(/`([^`]*)`/g, "$1")

      // Remove strikethrough
      .replace(/~~([^~]*)~~/g, "$1")

      // Remove blockquotes
      .replace(/^>\s+/gm, "")

      // Remove horizontal rules
      .replace(/^[-*_]{3,}$/gm, "")

      // Remove list markers
      .replace(/^\s*[-*+]\s+/gm, "")
      .replace(/^\s*\d+\.\s+/gm, "")

      // Remove extra whitespace and normalize
      .replace(/\s+/g, " ")
      .replace(/[.!?,;\s]+$/, "")
      .trim()
  );
}
